import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  TrendingUp, TrendingDown, Calendar, Building2, ExternalLink, 
  ArrowLeft, BarChart3, Activity, Search, Bookmark, Share2,
  Clock, DollarSign, Target, Zap
} from 'lucide-react';
import { Chart, registerables } from 'chart.js';

// --- 1. REAL PROJECT IMPORTS (Uncomment these in your actual app) ---
/*
import { apiService } from '../services/api';
import { 
  formatCurrency, 
  formatDate, 
  formatPercentage, 
  getRecommendationBadgeClass, 
  getStockScoreBadgeClass, 
  getStockScoreColor 
} from '../utils';
import { LoadingState } from '../components/LoadingSpinner';
import { ErrorState } from '../components/ErrorBoundary';
import PriceChart from '../components/PriceChart';
*/
import { Stock, Recommendation, AnalystTrend, PriceHistory } from '../types';
import { apiService } from '../services/api';
import { formatCurrency, formatDate, formatPercentage, getRecommendationBadgeClass, getStockScoreBadgeClass, getStockScoreColor } from '../utils';
import { LoadingState } from '../components/LoadingSpinner';
import { ErrorState } from '../components/ErrorBoundary';
import PriceChart  from '../components/PriceChart';

// --- 2. LOCAL UTILS & COMPONENTS (For Preview Only - Remove in Production) ---

// Register Chart.js
Chart.register(...registerables);

// Mock API Service




// Inline PriceChart (Matches your dark theme)


// --- 3. MAIN COMPONENT (With Your Logic Merged) ---

export const StockDetail = () => {
  // Use params from router, or default for preview
  const params = useParams(); 
  const symbol = params.symbol || "RELIANCE"; 
  const navigate = useNavigate ? useNavigate() : () => {};

  // State from your snippet
  const [stock, setStock] = useState<Stock | null>(null);
  const [recommendations, setRecommendations] = useState<Recommendation[]>([]);
  const [analystTrends, setAnalystTrends] = useState<AnalystTrend[]>([]);
  const [priceHistory, setPriceHistory] = useState<PriceHistory[]>([]);
  const [stockData, setStockData] = useState<any>(null);
  const [stats, setStats] = useState<any>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [showAllRecos, setShowAllRecos] = useState(false);
  const [activeTab, setActiveTab] = useState('daily');

  useEffect(() => {
    if (symbol) {
      fetchStockData();
    }
  }, [symbol]);

  // --- LOGIC FROM YOUR SNIPPET ---
  const fetchStockData = async () => {
    try {
      setLoading(true);
      setError(null);

      console.log('Fetching stock data for symbol:', symbol);
      
      // 1. Try Live Details First
      try {
        const liveResponse = await apiService.getLiveStockDetails(symbol);
        console.log('Live stock response:', liveResponse);
        
        if (liveResponse.success && liveResponse.data) {
          const liveData = liveResponse.data;
          
          setStockData(liveData);
          
          const transformedStock = {
            id: liveData.company.id,
            name: liveData.company.name,
            symbol: liveData.company.seo_name || symbol,
            exchange: 'NSE',
            sector: liveData.company.sector || 'N/A',
            current_price: parseFloat(String(liveData.live_data?.nse?.current || liveData.live_data?.bse?.current || '0')),
            last_updated: liveData.fetched_at,
            market_cap: liveData.peer_comparison?.current_stock?.market_cap || 0,
            company_id: liveData.company.id,
            currency: 'INR',
            created_at: liveData.fetched_at,
            updated_at: liveData.fetched_at
          };
          
          setStock(transformedStock);
          
          if (liveData.recommendations) {
            setRecommendations(liveData.recommendations);
          }
          
          if (liveData.stats) {
            setStats(liveData.stats);
          }
          
          if (liveData.price_history) {
            const dailyData = liveData.price_history.daily || [];
            const priceData = dailyData.map((item: any) => ({
              ...item,
              price: item.close || item.price || 0,
              stockId: liveData.company.id,
              id: item.timestamp
            }));
            setPriceHistory(priceData);
          }
          
          // Fetch trends in parallel if needed, or set placeholder
          const trendsResponse = await apiService.getAnalystTrends(transformedStock.id).catch(() => ({ success: false, data: [] }));
          if (trendsResponse.success) setAnalystTrends(trendsResponse.data);
          
          setLoading(false);
          return;
        }
      } catch (liveError) {
        console.warn('Live details not available:', liveError);
      }
      
      // 2. Fallback Logic
      const stockResponse = await apiService.getStockBySymbol(symbol);
      
      if (stockResponse.success && stockResponse.data) {
        const sData = stockResponse.data as any;
        
        const transformedStock = {
          id: sData.company?.id || sData.company_id,
          name: sData.company?.name || 'Unknown Company',
          symbol: sData.company?.seo_name || symbol,
          exchange: 'NSE',
          sector: sData.company?.sector || sData.sector || 'N/A',
          current_price: parseFloat(String(sData.current_price || '0')),
          last_updated: sData.updated_at || new Date().toISOString(),
          market_cap: sData.market_cap || 0,
          company_id: sData.company_id,
          currency: 'INR',
          created_at: sData.created_at || new Date().toISOString(),
          updated_at: sData.updated_at || new Date().toISOString(),
        };
        
        setStock(transformedStock);
        
        const [recoResponse, trendsResponse, priceResponse] = await Promise.all([
          apiService.getRecommendations({ query: symbol }).catch(err => ({ success: false, data: [] })),
          apiService.getAnalystTrends(transformedStock.id).catch(err => ({ success: false, data: [] })),
          apiService.getPriceHistory(transformedStock.id, 30).catch(err => ({ success: false, data: [] }))
        ]);

        if (recoResponse.success) setRecommendations(recoResponse.data);
        if (trendsResponse.success) setAnalystTrends(trendsResponse.data);
        if (priceResponse.success) setPriceHistory(priceResponse.data);
      } else {
        setError(`Stock "${symbol}" not found.`);
      }
    } catch (err) {
      console.error('Error fetching stock data:', err);
      setError(`Error loading stock: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <LoadingState />;
  if (error) return <ErrorState message={error} onRetry={fetchStockData} />;
  if (!stock) return <ErrorState message="Stock not found" />;

  const latestTrend = analystTrends[0];
  const displayedRecos = showAllRecos ? recommendations : recommendations.slice(0, 5);
  
  // Logic from your snippet for scores
  const companyScore = (
    (stockData?.company?.stock_score) ||
    (recommendations?.[0])?.stock_score ||
    stats?.stock_score ||
    null
  );

  // --- RENDER (Using Dark Mode UI) ---
  return (
    <div className="min-h-screen bg-[#05070A] text-gray-300 font-sans selection:bg-emerald-500/30">
      
      {/* Top Navigation Bar */}
      <div className="border-b border-white/5 bg-[#05070A]/80 backdrop-blur-md sticky top-0 z-40">
        <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <button onClick={() => window.history.back()} className="flex items-center gap-2 text-sm font-medium text-gray-400 hover:text-white transition-colors">
            <ArrowLeft className="w-4 h-4" />
            <span>Back</span>
          </button>
          <div className="flex items-center gap-3">
             <button className="p-2 text-gray-400 hover:text-white hover:bg-white/5 rounded-lg transition-all">
                <Bookmark className="w-4 h-4" />
             </button>
             <button className="p-2 text-gray-400 hover:text-white hover:bg-white/5 rounded-lg transition-all">
                <Share2 className="w-4 h-4" />
             </button>
          </div>
        </div>
      </div>

      <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-8">
        
        {/* Header Section */}
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 pb-6 border-b border-white/5">
          <div className="space-y-4">
             <div className="flex items-center gap-3">
                <span className="px-2 py-1 rounded text-[10px] font-bold uppercase tracking-wider bg-white/5 text-gray-400 border border-white/10">
                  {stock.exchange}
                </span>
                <span className="px-2 py-1 rounded text-[10px] font-bold uppercase tracking-wider bg-white/5 text-gray-400 border border-white/10">
                  {stock.sector}
                </span>
             </div>
             <div>
               <h1 className="text-4xl md:text-5xl font-black text-white tracking-tight mb-1">{stock.name}</h1>
               <div className="flex items-baseline gap-2 text-lg text-gray-500 font-medium">
                  <span>{stock.symbol}</span>
                  {companyScore && (
                    <span className={`ml-4 text-xs font-bold px-2 py-0.5 rounded border ${
                      Number(companyScore) >= 7 ? 'text-emerald-400 border-emerald-500/30 bg-emerald-500/10' : 
                      Number(companyScore) >= 4 ? 'text-yellow-400 border-yellow-500/30 bg-yellow-500/10' : 
                      'text-red-400 border-red-500/30 bg-red-500/10'
                    }`}>
                      SCORE: {Number(companyScore).toFixed(1)}
                    </span>
                  )}
               </div>
             </div>
          </div>

          <div className="flex flex-col items-start lg:items-end">
            <div className="text-5xl font-mono font-medium text-white tracking-tighter">
              {formatCurrency(stock.current_price)}
            </div>
            <div className="text-xs text-gray-500 mt-2 font-mono uppercase tracking-widest flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></span>
              Live Data • {formatDate(stock.last_updated)}
            </div>
          </div>
        </div>

        {/* Quick Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard 
            label="Market Cap" 
            value={stock.market_cap && stock.market_cap > 0 ? `₹${(stock.market_cap / 100000).toFixed(2)}L Cr` : 'N/A'} 
            icon={<Building2 className="w-4 h-4 text-blue-400" />}
          />
          <StatCard 
            label="Analyst Coverage" 
            value={recommendations.length} 
            subValue="Active Reports"
            icon={<Activity className="w-4 h-4 text-purple-400" />}
          />
          <StatCard 
            label="Buy Ratings" 
            value={recommendations.filter(r => (r.recommendation_type || r.recommendation || '').toLowerCase().includes('buy')).length}
            subValue="Strong Signal"
            icon={<TrendingUp className="w-4 h-4 text-emerald-400" />}
            highlight="emerald"
          />
          <StatCard 
            label="Avg Upside" 
            value={formatPercentage(stats?.avg_upside || 0)} 
            subValue="Target Potential"
            icon={<Target className="w-4 h-4 text-yellow-400" />}
          />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          
          {/* LEFT COLUMN (Main) */}
          <div className="lg:col-span-2 space-y-8">
            
            {/* Price Chart Section */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-bold text-white flex items-center gap-2">
                  <BarChart3 className="w-5 h-5 text-emerald-500" />
                  Price Action
                </h3>
                <div className="flex bg-white/5 rounded-lg p-1 border border-white/5">
                  {['daily', 'intraday'].map(tab => (
                    <button
                      key={tab}
                      onClick={() => {
                        setActiveTab(tab);
                        const key = tab === 'daily' ? 'daily' : 'intraday';
                        if (stockData?.price_history?.[key]) {
                          const mapped = stockData.price_history[key].map((item: any) => ({
                             ...item, 
                             price: item.close || item.price || 0,
                             id: item.timestamp
                          }));
                          setPriceHistory(mapped);
                        }
                      }}
                      className={`px-4 py-1.5 text-xs font-bold uppercase tracking-wider rounded-md transition-all ${
                        activeTab === tab 
                        ? 'bg-emerald-600 text-white shadow-lg shadow-emerald-900/20' 
                        : 'text-gray-500 hover:text-gray-300'
                      }`}
                    >
                      {tab}
                    </button>
                  ))}
                </div>
              </div>
              
              <div className="h-[400px] w-full bg-[#0A0C10] border border-white/5 rounded-2xl p-4">
                <PriceChart priceHistory={priceHistory} />
              </div>
            </div>

            {/* Recommendations Table */}
            <div className="bg-[#0A0C10] rounded-2xl border border-white/5 overflow-hidden">
               <div className="p-6 border-b border-white/5 flex items-center justify-between">
                  <h3 className="text-lg font-bold text-white flex items-center gap-2">
                    <Zap className="w-5 h-5 text-yellow-500" />
                    Brokerage Insights
                  </h3>
                  {recommendations.length > 5 && (
                    <button 
                      onClick={() => setShowAllRecos(!showAllRecos)}
                      className="text-xs font-bold text-emerald-500 hover:text-emerald-400 uppercase tracking-wider"
                    >
                      {showAllRecos ? 'Show Less' : 'View All'}
                    </button>
                  )}
               </div>
               
               <div className="overflow-x-auto">
                 <table className="w-full">
                   <thead>
                     <tr className="bg-white/[0.02] border-b border-white/5">
                       <th className="text-left py-4 px-6 text-[11px] font-bold uppercase tracking-wider text-gray-500">Firm</th>
                       <th className="text-left py-4 px-6 text-[11px] font-bold uppercase tracking-wider text-gray-500">Signal</th>
                       <th className="text-left py-4 px-6 text-[11px] font-bold uppercase tracking-wider text-gray-500">Target</th>
                       <th className="text-left py-4 px-6 text-[11px] font-bold uppercase tracking-wider text-gray-500">Upside</th>
                       <th className="text-left py-4 px-6 text-[11px] font-bold uppercase tracking-wider text-gray-500">Date</th>
                       <th className="text-right py-4 px-6 text-[11px] font-bold uppercase tracking-wider text-gray-500">Action</th>
                     </tr>
                   </thead>
                   <tbody className="divide-y divide-white/5">
                     {displayedRecos.map((reco, idx) => {
                       const type = reco.recommendation_type || reco.recommendation || reco.reco_type || 'N/A';
                       const upside = reco.upside_potential || reco.potential_upside_percent || 0;
                       const target = reco.target_price || reco.targetPrice || 0;
                       const date = reco.recommendation_date || reco.created_at;
                       const orgName = reco.organization_name || reco.brokerage?.name || 'Unknown';

                       return (
                         <tr key={idx} className="hover:bg-white/[0.02] transition-colors group">
                           <td className="py-4 px-6">
                             <div className="font-medium text-gray-200">
                               {orgName}
                             </div>
                           </td>
                           <td className="py-4 px-6">
                             <span className={`inline-flex items-center px-2.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wide border ${getRecommendationBadgeClass(type)}`}>
                               {type.replace('_', ' ')}
                             </span>
                           </td>
                           <td className="py-4 px-6 font-mono text-gray-300">
                             {target > 0 ? formatCurrency(target) : '-'}
                           </td>
                           <td className="py-4 px-6">
                             <div className={`flex items-center font-bold text-sm ${upside > 0 ? 'text-emerald-500' : 'text-red-500'}`}>
                               {upside > 0 ? <TrendingUp className="w-3 h-3 mr-1" /> : <TrendingDown className="w-3 h-3 mr-1" />}
                               {formatPercentage(Math.abs(upside))}
                             </div>
                           </td>
                           <td className="py-4 px-6 text-sm text-gray-500">
                             {formatDate(date)}
                           </td>
                           <td className="py-4 px-6 text-right">
                             {(reco.pdf_url || reco.reportUrl) && (
                               <a 
                                 href={reco.pdf_url || reco.reportUrl} 
                                 target="_blank" 
                                 rel="noreferrer"
                                 className="inline-flex items-center justify-center p-1.5 rounded-lg bg-white/5 hover:bg-white/10 text-blue-400 transition-colors"
                               >
                                 <ExternalLink className="w-3 h-3" />
                               </a>
                             )}
                           </td>
                         </tr>
                       );
                     })}
                   </tbody>
                 </table>
               </div>
            </div>
          </div>

          {/* RIGHT COLUMN (Sidebar) */}
          <div className="space-y-6">
            
            {/* Stock Score Widget */}
            <div className="bg-[#0A0C10] rounded-2xl border border-white/5 p-6 relative overflow-hidden group">
               <div className="absolute top-0 right-0 p-4 opacity-10 group-hover:opacity-20 transition-opacity">
                 <Activity className="w-24 h-24 text-white" />
               </div>
               <h3 className="text-sm font-bold text-gray-400 uppercase tracking-widest mb-6 relative z-10">AI Stock Score</h3>
               
               <div className="flex items-end gap-2 mb-2 relative z-10">
                 <span className={`text-6xl font-black tracking-tighter ${getStockScoreColor(Number(companyScore))}`}>
                   {companyScore ? Number(companyScore).toFixed(1) : 'N/A'}
                 </span>
                 <span className="text-lg font-medium text-gray-500 mb-2">/ 10</span>
               </div>
               
               <div className="h-2 w-full bg-white/5 rounded-full mb-6 relative z-10">
                 <div 
                    className={`h-full rounded-full transition-all duration-1000 ease-out ${
                      Number(companyScore) >= 7 ? 'bg-emerald-500' : 
                      Number(companyScore) >= 4 ? 'bg-yellow-500' : 'bg-red-500'
                    }`}
                    style={{ width: `${(Number(companyScore) || 0) * 10}%` }} 
                 />
               </div>

               {/* Score Breakdown (if available in recos) */}
               {recommendations[0]?.stock_score_data && (
                 <div className="space-y-3 relative z-10 mt-6 pt-6 border-t border-white/5">
                    {Object.entries(recommendations[0].stock_score_data).filter(([k]) => k !== 'overall').slice(0, 4).map(([k, v]) => (
                      <div key={k} className="flex justify-between items-center text-xs">
                        <span className="capitalize text-gray-500">{k.replace('_', ' ')}</span>
                        <span className="font-mono font-bold text-gray-300">{Number(v).toFixed(1)}</span>
                      </div>
                    ))}
                 </div>
               )}
            </div>

            {/* Analyst Consensus Widget */}
            {latestTrend && (
              <div className="bg-[#0A0C10] rounded-2xl border border-white/5 p-6">
                 <h3 className="text-sm font-bold text-gray-400 uppercase tracking-widest mb-6">Analyst Consensus</h3>
                 
                 <div className="space-y-4">
                    <ConsensusBar label="Strong Buy" value={latestTrend.strongBuy} total={latestTrend.totalAnalysts} color="bg-emerald-500" />
                    <ConsensusBar label="Buy" value={latestTrend.buy} total={latestTrend.totalAnalysts} color="bg-emerald-600/70" />
                    <ConsensusBar label="Hold" value={latestTrend.hold} total={latestTrend.totalAnalysts} color="bg-yellow-500" />
                    <ConsensusBar label="Sell" value={latestTrend.sell} total={latestTrend.totalAnalysts} color="bg-red-500" />
                 </div>
                 
                 <div className="mt-6 pt-4 border-t border-white/5 flex justify-between items-center">
                    <span className="text-xs text-gray-500 uppercase">Total Analysts</span>
                    <span className="text-xl font-bold text-white">{latestTrend.totalAnalysts}</span>
                 </div>
              </div>
            )}

            {/* Target Price Widget */}
            {stats && (
              <div className="bg-gradient-to-br from-[#0A0C10] to-[#0F1218] rounded-2xl border border-white/5 p-6 shadow-2xl">
                 <h3 className="text-sm font-bold text-gray-400 uppercase tracking-widest mb-4">Price Targets</h3>
                 
                 <div className="space-y-4">
                   <div className="flex justify-between items-center p-3 bg-white/5 rounded-lg border border-white/5">
                      <span className="text-xs text-gray-400 uppercase">Average</span>
                      <span className="font-mono font-bold text-white">{formatCurrency(stats.avg_target_price || 0)}</span>
                   </div>
                   
                   <div className="grid grid-cols-2 gap-3">
                     <div className="p-3 bg-white/5 rounded-lg border border-white/5 text-center">
                        <div className="text-[10px] text-gray-500 uppercase mb-1">Max Upside</div>
                        <div className="font-bold text-emerald-500">{formatPercentage(stats.max_upside || 0)}</div>
                     </div>
                     <div className="p-3 bg-white/5 rounded-lg border border-white/5 text-center">
                        <div className="text-[10px] text-gray-500 uppercase mb-1">Min Upside</div>
                        <div className="font-bold text-yellow-500">{formatPercentage(stats.min_upside || 0)}</div>
                     </div>
                   </div>
                 </div>
              </div>
            )}

          </div>
        </div>
      </div>
    </div>
  );
};

// --- SUB-COMPONENTS ---
interface StatCardProps {
  label: string;
  value: string | number;
  subValue?: string;
  icon: React.ReactNode;
  highlight?: string;
}

const StatCard: React.FC<StatCardProps> = ({ label, value, subValue, icon, highlight }) => (
  <div className="bg-[#0A0C10] border border-white/5 rounded-xl p-5 hover:border-white/10 transition-colors group">
    <div className="flex items-start justify-between mb-2">
      <span className="text-xs font-bold text-gray-500 uppercase tracking-wider">{label}</span>
      <div className={`p-1.5 rounded-lg bg-white/5 group-hover:bg-white/10 transition-colors ${highlight === 'emerald' ? 'text-emerald-500' : 'text-gray-400'}`}>
        {icon}
      </div>
    </div>
    <div className="text-xl font-bold text-white tracking-tight">{value}</div>
    {subValue && <div className="text-[10px] font-medium text-gray-600 mt-1">{subValue}</div>}
  </div>
);

interface ConsensusBarProps {
  label: string;
  value: number;
  total: number;
  color: string;
}

const ConsensusBar: React.FC<ConsensusBarProps> = ({ label, value, total, color }) => (
  <div className="flex items-center gap-3 text-xs">
    <span className="w-20 text-gray-400 font-medium">{label}</span>
    <div className="flex-1 h-2 bg-white/5 rounded-full overflow-hidden">
      <div className={`h-full rounded-full ${color}`} style={{ width: `${total > 0 ? (value / total) * 100 : 0}%` }} />
    </div>
    <span className="w-6 text-right font-bold text-gray-300">{value}</span>
  </div>
);

export default StockDetail;